import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import _ from 'lodash'
import { IDoesFilterPassParams } from 'ag-grid-community'
import { Box, styled } from '@mui/material'
import { CustomFilterProps, useGridFilter } from 'ag-grid-react'
import { APIResponse } from '../../../../../../lib/commons/api'
import { BulkSheetFilter, FilterFooter } from '../common'
import { useSelector } from 'react-redux'
import { AllState } from '../../../../../../store'
import usePrevious from '../../../../../hooks/usePrevious'
import { ReferencedEntityWithIcon } from '../../../../../../domain/value-object/ReferencedEntity'
import {
  EntitySearchFilter,
  EntitySearchFilterType,
  getEntitySearchFilterTypeLabel,
} from './EntitySearchFilterModel'
import { FilterAutocomplete } from './FilterAutocomplete'
import { SelectedValues } from './SelectedValues'
import { FilterTypeSelect } from './FilterTypeSelect'

type Props = CustomFilterProps & {
  searchOptions: (
    params: any,
    text?: string
  ) => Promise<ReferencedEntityWithIcon[]>
  fetch: (v: EntitySearchFilter) => Promise<APIResponse>
}

const buildEntitySearchFilterModel = (
  filterType: EntitySearchFilterType,
  selectedValues: ReferencedEntityWithIcon[]
): EntitySearchFilter | null => {
  switch (filterType) {
    case EntitySearchFilterType.ALL:
      return null
    case EntitySearchFilterType.BLANK:
      return { filterType, selectedValues: [] }
    case EntitySearchFilterType.SELECTED_VALUES:
      return selectedValues.length > 0 ? { filterType, selectedValues } : null
  }
}

export * from './EntitySearchFilterModel'
export const ServerSideEntitySearchFilter = (params: Props) => {
  const {
    model,
    onModelChange,
    api,
    searchOptions: _searchOptions,
    fetch: fetchFn,
    context,
  } = params
  const hasRequiredSaveData = useSelector<AllState>(
    state => state.hasRequiredSaveData.hasRequiredSaveData
  )
  const prevHasRequiredSaveData = usePrevious(hasRequiredSaveData)

  // Derive state from model (single source of truth)
  const filterType =
    (model as EntitySearchFilter | null)?.filterType ??
    EntitySearchFilterType.ALL
  const EMPTY_VALUES: ReferencedEntityWithIcon[] = useMemo(() => [], [])
  const selectedValues =
    (model as EntitySearchFilter | null)?.selectedValues ?? EMPTY_VALUES

  // UI-local state for async filtering
  const [loading, isLoading] = useState(false)
  const [filteredIds, setFilteredIds] = useState<Set<string> | undefined>(
    undefined
  )

  const addedRowIds = useRef<string[]>()
  useEffect(() => {
    if (addedRowIds.current) return
    addedRowIds.current = []
  }, [])

  const fetchFilteredIds = async (v: EntitySearchFilter) => {
    const response = await fetchFn(v)
    setFilteredIds(new Set(response.json))
  }

  const search = useMemo(
    () =>
      _.debounce(async (v: EntitySearchFilter | null) => {
        if (!v) {
          setFilteredIds(undefined)
          return
        }
        try {
          isLoading(true)
          await fetchFilteredIds(v)
        } finally {
          isLoading(false)
        }
      }, 300),
    []
  )

  // When criteria changes (model changes), trigger debounced search
  const modelKey = JSON.stringify(model)
  // eslint-disable-next-line react-hooks/exhaustive-deps -- modelKey は model の値比較用。model 自体は毎レンダーで新規オブジェクトになるため deps に入れると毎回発火する。search は useMemo で安定
  useEffect(() => {
    search(model as EntitySearchFilter | null)
  }, [modelKey])

  // When filteredIds changes, trigger re-filtering
  // eslint-disable-next-line react-hooks/exhaustive-deps -- api は AG Grid から渡される安定参照。filteredIds 変更時のみ再フィルタリングをトリガーする
  useEffect(() => {
    api.onFilterChanged()
  }, [filteredIds])

  // Re-fetch after save
  // eslint-disable-next-line react-hooks/exhaustive-deps -- hasRequiredSaveData の変化のみをトリガーとし、その時点の最新 model/fetchFilteredIds を読む
  useEffect(() => {
    const isAfterSavedOrRefreshed =
      prevHasRequiredSaveData && !hasRequiredSaveData
    if (isAfterSavedOrRefreshed && model) {
      fetchFilteredIds(model as EntitySearchFilter).then(() => {
        addedRowIds.current = []
      })
    }
  }, [hasRequiredSaveData])

  const searchOptions = useCallback(
    async (text?: string) => {
      return _searchOptions(params, text)
    },
    [params, _searchOptions]
  )

  const onSelectedValuesChange = useCallback(
    (value: ReferencedEntityWithIcon[] | null) => {
      onModelChange(buildEntitySearchFilterModel(filterType, value || []))
    },
    [filterType, onModelChange]
  )

  const onFilterTypeChange = useCallback(
    (newFilterType: EntitySearchFilterType) => {
      // SELECTED_VALUES 以外に切り替えたら selectedValues をクリア
      const newSelectedValues =
        newFilterType !== EntitySearchFilterType.SELECTED_VALUES
          ? []
          : selectedValues
      onModelChange(
        buildEntitySearchFilterModel(newFilterType, newSelectedValues)
      )
    },
    [selectedValues, onModelChange]
  )

  useGridFilter({
    doesFilterPass(params: IDoesFilterPassParams) {
      // Don't filter new rows
      if (params.data.added) {
        if (addedRowIds.current && params.node.id) {
          addedRowIds.current.push(params.node.id)
        }
        return true
      }
      if (
        addedRowIds.current &&
        params.node.id &&
        addedRowIds.current.includes(params.node.id)
      ) {
        return true
      }
      if (!filteredIds) return true
      return params.node.id ? filteredIds.has(params.node.id) : false
    },

    getModelAsString() {
      if (!model) return ''
      return getEntitySearchFilterTypeLabel(filterType)
    },
  })

  return (
    <BulkSheetFilter>
      <FilterBox>
        <FilterTypeSelect
          filterType={filterType}
          onChange={onFilterTypeChange}
        />
        <SelectedValues
          selectedValues={selectedValues}
          onChange={onSelectedValuesChange}
        />
        <FilterAutocomplete
          searchOptions={searchOptions}
          value={selectedValues}
          onChange={onSelectedValuesChange}
          disabled={filterType !== EntitySearchFilterType.SELECTED_VALUES}
        />
      </FilterBox>
      <FilterFooter loading={loading} onClick={() => onModelChange(null)} />
    </BulkSheetFilter>
  )
}
const FilterBox = styled(Box)({
  width: '100%',
  display: 'flex',
  justifyContent: 'start',
  flexDirection: 'column',
  margin: '0 3px',
})
